// src/pages/UserPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import UserInputRenderer from '../components/UserInputRenderer';
import '../components/UserInputRenderer.css';
import imageCompression from 'browser-image-compression';
import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { pdfjs } from 'react-pdf';

// Set the workerSrc to a reliable CDN to avoid local file issues
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const defaultPdfPath = '/rotary-youth-exchange.pdf';
const defaultTemplatePath = `${process.env.PUBLIC_URL}/template.json`;
const fontPath = `${process.env.PUBLIC_URL}/fonts/NotoSansTC.ttf`;
const submissionUrl = '/api/process-form';
const LOCAL_STORAGE_KEY = 'pdfFormData';

// Helper to convert ArrayBuffer to Base64
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function UserPage() {
  const [pdfFile] = useState(defaultPdfPath);
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [formFields, setFormFields] = useState([]); // State lifted from renderer
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fontBytes, setFontBytes] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [allowSignatureInDraft, setAllowSignatureInDraft] = useState(false); // New state for the feature
  const isInitialMount = useRef(true); // Re-introducing the ref for a simpler approach
  const formDataRef = useRef(formData);
  const timeoutRef = useRef(null);

  const handleAllowSignatureChange = (e) => {
    const isChecked = e.target.checked;
    setAllowSignatureInDraft(isChecked);

    if (!isChecked) {
      // When the user unchecks the box, clear any existing signature data from the form state.
      // This will also trigger the auto-save, which will then persist this cleared state.
      setFormData(currentData => {
        const newData = { ...currentData };
        Object.keys(newData).forEach(key => {
          if (newData[key]?.type === 'signature') {
            delete newData[key];
          }
        });
        return newData;
      });
    }
  };

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Pagination state
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const handleFormUpdate = (id, data) => {
    const now = new Date().toISOString();
    setFormData(prev => {
      let newFormData = { ...prev, [id]: { ...data, updatedAt: now } };
      const allGroups = template?.linkedFieldGroups;
      if (!allGroups) return newFormData;

      const updatedPdfField = formFields.find(f => f.uniqueId === id);
      const updatedBox = template.boxes.find(b => b.id === id);
      const identifier = updatedPdfField?.fieldName || updatedBox?.id;
      if (!identifier) return newFormData;

      const groupType = data.type === 'Btn' ? 'checkbox' : (data.type === 'text-fill' ? 'text-fill' : data.type);
      const groupsForType = allGroups[groupType];
      if (!groupsForType) return newFormData;

      let allIdentifiersInGroup = [];
      for (const groupName in groupsForType) {
        if (groupsForType[groupName].includes(identifier)) {
          allIdentifiersInGroup = groupsForType[groupName];
          break;
        }
      }

      if (allIdentifiersInGroup.length > 0) {
        // Create a mutable copy for updates
        let updatedFormData = { ...newFormData };

        // Update all fields whose fieldName or id is in the group
        formFields.forEach(field => {
          if (allIdentifiersInGroup.includes(field.fieldName)) {
            updatedFormData[field.uniqueId] = { 
              ...prev[field.uniqueId], 
              ...field, 
              value: data.value, 
              updatedAt: now 
            };
          }
        });
        template.boxes.forEach(box => {
          if (allIdentifiersInGroup.includes(box.id)) {
            updatedFormData[box.id] = { 
              ...prev[box.id],
              type: box.type, 
              value: data.value, 
              updatedAt: now 
            };
          }
        });
        newFormData = updatedFormData;
      }
      
      // Log sorted form data for debugging
      const sortedKeys = Object.keys(newFormData).sort();
      const sortedFormData = {};
      sortedKeys.forEach(key => {
        sortedFormData[key] = newFormData[key];
      });
      console.log('Updated FormData:', sortedFormData);

      return newFormData;
    });
  };

  const saveState = useCallback(() => {
    const dataToSave = Object.entries(formDataRef.current).reduce((acc, [key, value]) => {
      // Exclude images always. Exclude signatures only if the user hasn't allowed it.
      if (value.type !== 'image' && (value.type !== 'signature' || allowSignatureInDraft)) {
        acc[key] = value;
      }
      return acc;
    }, {});

    // Only save if there's data to save or if the setting is non-default
    if (Object.keys(dataToSave).length === 0 && !allowSignatureInDraft) return;

    const now = new Date();
    const savedData = {
      timestamp: now.toISOString(),
      data: dataToSave,
      settings: {
        allowSignatureInDraft: allowSignatureInDraft,
      },
    };

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(savedData));
    setLastSaved(now);
  }, [allowSignatureInDraft]);

  // Effect for initial loading of assets and data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [fontResponse, templateResponse] = await Promise.all([
          fetch(fontPath),
          fetch(defaultTemplatePath),
        ]);

        if (!fontResponse.ok) throw new Error('Failed to load font from CDN.');
        const loadedFontBytes = await fontResponse.arrayBuffer();
        setFontBytes(loadedFontBytes);

        if (!templateResponse.ok) throw new Error('Template file not found.');
        const loadedTemplate = await templateResponse.json();
        if (!loadedTemplate.boxes) throw new Error('Invalid template file.');
        setTemplate(loadedTemplate);

        const savedDataString = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (savedDataString) {
          const savedData = JSON.parse(savedDataString);
          const lastSavedDate = new Date(savedData.timestamp);
          if (window.confirm(`我們找到了您在 ${lastSavedDate.toLocaleString()} 儲存的資料，要載入嗎？`)) {
            setFormData(savedData.data || {});
            if (savedData.settings) {
              setAllowSignatureInDraft(savedData.settings.allowSignatureInDraft || false);
            }
            setLastSaved(lastSavedDate);
          }
        }
      } catch (err) {
        console.error("Failed to load initial data:", err);
        setError(err.message);
      }
    };

    loadInitialData();
  }, []); // Run only once on mount

  // Effect to prevent accidental navigation while submitting
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    if (isSubmitting) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isSubmitting]);

  // Effect for auto-saving form data to localStorage (debounced)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(saveState, 1000);
  }, [formData, saveState]);

  // Effect for saving on page hide or before unload
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveState();
      }
    };

    window.addEventListener('beforeunload', saveState);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', saveState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [saveState]);

  const handleImageUpload = async (id, file, rect = null, page = null, fieldName = null) => {
    const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true, initialQuality: 0.7 };
    try {
      const compressedFile = await imageCompression(file, options);
      const base64 = await imageCompression.getDataUrlFromFile(compressedFile);
      handleFormUpdate(id, { type: 'image', value: base64, rect, page, fieldName });
    } catch (error) {
      console.error('Image compression error:', error);
      alert('Failed to compress image.');
    }
  };

  const generatePdfBytes = async () => {
    // ... (generatePdfBytes function remains the same)
    if (!pdfFile || !template || !fontBytes) {
      throw new Error('PDF generation dependencies not ready.');
    }
    const pdfBuffer = await fetch(pdfFile).then(res => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(pdfBuffer);
    pdfDoc.registerFontkit(fontkit);
    const customFont = await pdfDoc.embedFont(fontBytes);
    const form = pdfDoc.getForm();
    const pages = pdfDoc.getPages();

    // New, robust logic for filling fields
    for (const key in formData) {
      const data = formData[key];
      if (!data || data.value === null || data.value === undefined) continue;

      const formField = formFields.find(f => f.uniqueId === key);
      if (!formField) continue; // Skip if it's not a PDF field (e.g., a drawable box)

      try {
        if (formField.type === 'Tx' || formField.type === 'Date') {
          form.getTextField(formField.fieldName).setText(data.value);
        } else if (formField.type === 'Ch') {
          form.getDropdown(formField.fieldName).select(data.value);
        } else if (formField.type === 'Btn') {
          // Checkboxes might have a value of 'Yes' or a boolean.
          // The `check()` method should be called if the value indicates a checked state.
          if (data.value && data.value !== 'Off') {
            form.getCheckBox(formField.fieldName).check();
          } else {
            form.getCheckBox(formField.fieldName).uncheck();
          }
        }
      } catch (e) {
        console.warn(`Could not process form field: ${formField.fieldName} (ID: ${key})`, e);
      }
    }

    // Handle checkbox fields with a specific method to avoid group-checking issues
    // Checkbox logic is now integrated into the main loop.
    // This section can be removed or left empty.
    
    // This loop now only needs to handle image drawing from formData.
    for (const key in formData) {
      const data = formData[key];
      if (!data || !data.value) continue;
      try {
        if (data.type === 'image' && data.rect && data.page) {
          const page = pages[data.page - 1];
          if (!page) continue;
          const [x1, y1, x2, y2] = data.rect;
          const boxWidth = x2 - x1;
          const boxHeight = y2 - y1;
          page.drawRectangle({ x: x1, y: y1, width: boxWidth, height: boxHeight, color: rgb(1, 1, 1) });
          const imageBytes = await (data.value.startsWith('data:image/png') ? pdfDoc.embedPng(data.value) : pdfDoc.embedJpg(data.value));
          const { width: imgWidth, height: imgHeight } = imageBytes.scale(1);
          const scale = Math.min(boxWidth / imgWidth, boxHeight / imgHeight);
          page.drawImage(imageBytes, { x: x1 + (boxWidth - imgWidth * scale) / 2, y: y1 + (boxHeight - imgHeight * scale) / 2, width: imgWidth * scale, height: imgHeight * scale });
        }
      } catch (e) {
        console.warn(`Could not process form field: ${data.fieldName || key}`, e);
      }
    }
    form.updateFieldAppearances(customFont);
    // 扁平化可以解決部分電腦不能開啟問題
    // form.flatten();

    for (const box of template.boxes) {
      const data = formData[box.id];
      if (!data || !data.value) continue;
      const page = pages[box.page - 1];
      if (!page) continue;
      const { x, y, width, height } = box;
      const y_pdf = page.getHeight() - y - height;
      if ((data.type === 'image' || data.type === 'signature') && !data.rect) {
        // Only draw a white background for regular images, not for signatures
        if (data.type === 'image') {
          page.drawRectangle({ x, y: y_pdf, width, height, color: rgb(1, 1, 1) });
        }
        const imageBytes = await (data.value.startsWith('data:image/png') ? pdfDoc.embedPng(data.value) : pdfDoc.embedJpg(data.value));
        const { width: imgWidth, height: imgHeight } = imageBytes.scale(1);
        const scale = Math.min(width / imgWidth, height / imgHeight);
        page.drawImage(imageBytes, { x: x + (width - imgWidth * scale) / 2, y: y_pdf + (height - imgHeight * scale) / 2, width: imgWidth * scale, height: imgHeight * scale });
      } else if (data.type === 'text-draw') {
        page.drawText(data.value, { x: x + 2, y: y_pdf + height - 14, size: 12, font: customFont, color: rgb(0, 0, 0), lineHeight: 14, maxWidth: width - 4 });
      }
    }
    return await pdfDoc.save();
  };

  const handlePdfDownload = async () => {
    setIsDownloading(true);
    try {
      const pdfBytes = await generatePdfBytes();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'filled-document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to generate PDF for download:', error);
      alert(`發生錯誤： ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = async () => {
    // ... (handleSubmit function remains the same)
    const findFieldValue = (fieldName) => {
      for (const key in formData) {
        if (formData[key].fieldName === fieldName) {
          return formData[key].value;
        }
      }
      return null;
    };
    const fullName = findFieldValue("Name You Wish to be Called");
    const emailAddress = findFieldValue("Email Address");

    if (!fullName || !emailAddress) {
      alert("驗證失敗：請務必填寫『您希望被稱呼的名字』和『電子郵件地址』欄位。");
      return; // Stop the submission
    }
    setIsSubmitting(true);
    try {
      const pdfBytes = await generatePdfBytes();
      const MAX_SIZE_MB = 48;
      const maxSizeBytes = MAX_SIZE_MB * 1024 * 1024;
      if (pdfBytes.length > maxSizeBytes) {
        alert(`錯誤：產生的 PDF 檔案大小 (${(pdfBytes.length / 1024 / 1024).toFixed(2)} MB) 超過 ${MAX_SIZE_MB} MB 的上限。請上傳較小或壓縮過的圖片。`);
        setIsSubmitting(false);
        return;
      }
      const pdfBase64 = arrayBufferToBase64(pdfBytes);
      const payload = {
        formData,
        pdfBase64,
      };
      const response = await fetch(submissionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      alert('送出成功！');
      // Clear saved data after successful submission
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setLastSaved(null);
    } catch (error) {
      console.error('Failed to submit form:', error);
      alert(`送出失敗： ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const previousPage = () => setPageNumber(p => Math.max(1, p - 1));
  const nextPage = () => setPageNumber(p => Math.min(numPages, p + 1));
  const progress = numPages ? (pageNumber / numPages) * 100 : 0;

  return (
    <div className="container">
      <div className="page-content" style={{ boxShadow: 'none', padding: 0, background: 'none' }}>
        {error ? (
          <div className="error-message"><p><strong>Error:</strong> {error}</p></div>
        ) : template ? (
          <>
            <div className="progress-container-main">
              <div className="progress-bar-main" style={{ width: `${progress}%` }}></div>
            </div>
            <div className="user-layout">
              <div className="pdf-panel">
                <UserInputRenderer 
                  pdfFile={pdfFile} 
                  template={template} 
                  formData={formData} 
                  onFormChange={handleFormUpdate}
                  formFields={formFields}
                  setFormFields={setFormFields}
                  handleImageUpload={handleImageUpload}
                  pageNumber={pageNumber}
                  setNumPages={setNumPages}
                />
              </div>
              <div className="pagination-panel-main">
                <button type="button" disabled={pageNumber <= 1 || isSubmitting || isDownloading} onClick={previousPage}>
                  <span className="desktop-icon">↑</span>
                  <span className="mobile-icon">←</span>
                </button>
                <button type="button" disabled={pageNumber >= numPages || isSubmitting || isDownloading} onClick={nextPage}>
                  <span className="desktop-icon">↓</span>
                  <span className="mobile-icon">→</span>
                </button>
                
                <div className="action-buttons-group">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div className="split-button-container">
                      <button 
                        type="button" 
                        className="submit-btn main" 
                        disabled={isSubmitting || isDownloading || !fontBytes} 
                        onClick={handleSubmit}
                      >
                        {isSubmitting ? '送出中...' : '送出'}
                      </button>
                      <button 
                        type="button" 
                        className="submit-btn dropdown-toggle" 
                        disabled={isSubmitting || isDownloading || !fontBytes}
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                      >
                        ▼
                      </button>
                      {dropdownOpen && (
                        <div className="dropdown-menu">
                          <button 
                            onClick={() => {
                              handlePdfDownload();
                              setDropdownOpen(false);
                            }}
                            disabled={isDownloading}
                          >
                            {isDownloading ? '下載中...' : '下載 PDF'}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="draft-options" style={{ marginTop: '10px' }}>
                      <label style={{ fontSize: '12px', color: '#6c757d', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={allowSignatureInDraft}
                          onChange={handleAllowSignatureChange}
                          style={{ marginRight: '5px' }}
                        />
                        允許儲存簽名草稿
                      </label>
                    </div>
                    {lastSaved && (
                      <span style={{ fontSize: '10px', color: '#6c757d', marginTop: '4px' }}>
                        上次儲存於：{lastSaved.toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <p>正在載入...</p>
        )}
      </div>
    </div>
  );
}

export default UserPage;